<template>
    <Rows-container 
        ref="rowsContainer" 
        :items-props="allLines"
    >
        <template v-for="(item, index) in allLines" :key="item.lineId">
            <line-view-skeleton v-if="item.cardItems === null" :name="item.skeleton" />
            <row-view
                v-if="checkExist(item)"
                :ref="item.ref"
                :index-row="index"
                :key-data="item.keyData"
                :item="item"
                :items="item.cardItems"
                :line-id="item.lineId"
                :title="item.title"
                :is-loading-next-page="item.isLoadingNextPage"
                :play-item-id="playItemId"
                @list-item-selected="itemsSelected"
                @load-next-page="loadNextPage"
                @load-previous-page="loadPreviousPage"
            />
        </template>
    </Rows-container>
</template>

<script setup lang="ts">
    import { type PropType, type Ref, type ComputedRef, ref, computed, watch, onUnmounted } from "vue";
    import { AppConstants } from "@/common";
    import { Strings } from "@/locales/types/enums";
    import { type PlayItemRestrictionsProcessor} from "../types/PlayItemRestrictionsProcessor";
    import { useI18n } from "vue-i18n";
    import { ComponentName as SkeletonName } from "@/components/Skeleton/types";
    import { usePlayerStore } from "@/stores/Player";
    import { storeToRefs } from 'pinia';
    import type { RowConfig, IEventSelected } from "@/components/RowView/types";
    import LineViewSkeleton from "@/components/Skeleton/LineViewSkeleton.vue";
    import type { RowsContainerRef } from "@/components/RowsContainer/types";
    import RowsContainer from "@/components/RowsContainer/RowsContainer.vue";
    import type { ExposeInterface } from "./types";
    import { useParentControl } from "@/composables/parentControl/useParentControl";
    import { useParentControlStore } from "@/stores/ParentControl";


    const PlayerStoreModule = usePlayerStore();
    const {
        getIsLoadingEpisodes: isLoadingEpisodes,
        playProgram,
        playChannel,
        getChannelsCards,
        getEpisodesCards
    } = storeToRefs(PlayerStoreModule);
    const {
        getRelatedCards,
        clearPageChannelCards,
        loadNextChannelPage,
        loadPreviousChannelPage,
        initChannelCards,
        initEpisodesCards,
        loadPreviousEpisodesPage,
        loadNextEpisodesPage,
    } = PlayerStoreModule;

    const ParentControlStoreModule = useParentControlStore();
    const {
        ShowParentModal,
        SetTargetWidgetAdultControl,
        SetIncorrectAdultPin,
    } = ParentControlStoreModule;
    const {
        getIsAdultPinCorrect: isAdultPinCorrect,
        getTargetWidgetAdultControl: targetWidgetAdultControl,
    } = storeToRefs(ParentControlStoreModule);


    const props = defineProps({
        isSeries: {
            type: Boolean,
            required: true,
        },
        isLive: {
            type: Boolean,
            required: true
        },
        itemProcessor: {
			type: Object as PropType<PlayItemRestrictionsProcessor | null>,
        	required: true
      	},
    });

    const { t } = useI18n();
    const { setCurrentTimes, checkParentControlCard } = useParentControl();
    const emit = defineEmits(['list-item-selected']);
    const rowsContainer: Ref<RowsContainerRef | null> = ref(null);
    const selectedItem: Ref<IEventSelected | null> = ref(null);
    const { shelfKeys }: typeof AppConstants = AppConstants;
    const name = "PlayerLines";

    const isPlayingChannel = computed((): boolean => !!props.isLive || !!props.itemProcessor?.isVCloud || !!props.itemProcessor?.isRestart)
    const playItemId = computed((): number | string | null => {
        if (isPlayingChannel.value) {
            return playChannel.value?.id ?? playProgram.value?.recentAirTime?.channelId ?? null;
        }
        if (props.isSeries) {
            return playProgram.value?.id ?? null;
        }
        return null;
    })
    const allLines: ComputedRef<RowConfig[]> = computed(() => {
        const items = [];
        if (isPlayingChannel.value) {
            items.push({
                keyData: shelfKeys.playerChannelsCards,
                title: t(Strings.PLAYER_CHANNELS).toString(),
                ref: shelfKeys.playerChannelsCards,
                lineId: shelfKeys.playerChannelsCards,
                skeleton: SkeletonName.PROGRAM_CARD_SKELETON,
                cardItems: getChannelsCards.value,
            });
        } else if (props.isSeries) {
            items.push({
                keyData: shelfKeys.playerEpisodesCards,
                title: t(Strings.PLAYER_EPISODES).toString(),
                ref: shelfKeys.playerEpisodesCards,
                lineId: shelfKeys.playerEpisodesCards,
                skeleton: SkeletonName.PROGRAM_CARD_SKELETON,
                cardItems: getEpisodesCards.value,
                isLoadingNextPage: isLoadingEpisodes.value,
            });
        } else {
            items.push({
                keyData: shelfKeys.playerRelatedCards,
                title: t(Strings.DETAILS_RELATED_CONTENT_TITLE).toString(),
                ref: shelfKeys.playerRelatedCards,
                lineId: shelfKeys.playerRelatedCards,
                skeleton: SkeletonName.PROGRAM_CARD_SKELETON,
                cardItems: getRelatedCards(),
            });
        }
        return items;
    });

    const checkExist = (item: RowConfig): boolean => item.cardItems !== null && item.cardItems.length > 0;

    const onListItemSelected = (): void => {
        emit("list-item-selected", selectedItem.value);
    };

    const itemsSelected = (event: IEventSelected) =>{
        const itemId = event.item.getId();
        switch (event.lineId) {
            case shelfKeys.playerEpisodesCards:
                if(playProgram.value && (String(playProgram.value.id) === itemId)) {
                    return;
                }
                
                break;
        
            case shelfKeys.playerChannelsCards:
                if(
                    playChannel.value && (String(playChannel.value.id) === itemId) ||
                    playProgram.value && (String(playProgram.value.recentAirTime?.channelId) === itemId)
                ) {
                    return;
                }

                break;
        }
        selectedItem.value = event;
        checkParentControlCard(event).then((isAllow)=>{
            if (isAllow) {
                onListItemSelected();
            } else {
                ShowParentModal();
                SetTargetWidgetAdultControl(name);
            }
        });
    };
    
    const loadNextPage = async (lineId: string): Promise<void> => {
        if(shelfKeys.playerChannelsCards === lineId) {
            loadNextChannelPage();
        }

        if(shelfKeys.playerEpisodesCards === lineId) {
            await loadNextEpisodesPage();
        }
    };

    const loadPreviousPage = (lineId: string): void => {
        if(shelfKeys.playerChannelsCards === lineId) {
            loadPreviousChannelPage();
        }

        if(shelfKeys.playerEpisodesCards === lineId) {
            loadPreviousEpisodesPage();
        }
    };

    const focusFirstRow = (): void => {
        if (rowsContainer.value) {
            rowsContainer.value.focusFirstRow();
        }
    };

    const initializeCards = (): void => {
        if (isPlayingChannel.value) {
            initChannelCards();
        }
        
        if (props.isSeries) {
            initEpisodesCards();
        }
    };

    watch(
        isAdultPinCorrect,
        (value: boolean): void => {
            if (value && name === targetWidgetAdultControl.value) {
                SetIncorrectAdultPin();
                setCurrentTimes();
                onListItemSelected();
            }
        }
    );

    onMounted((): void => {
        initializeCards();
    });

    onUnmounted((): void => {
        clearPageChannelCards();
    });

    defineExpose<ExposeInterface>({
        focusFirstRow
    });
</script>